import {
  SearchFilterCondition,
  SearchParameters,
  SearchUserEnriched,
} from 'lib-search-types';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMount, useUpdateEffect } from 'react-use';

import { krakenApi } from 'apis/kraken';
import { AwayStatus, presenceApi } from 'apis/presence';
import { userModalActions } from 'components/usersModal';
import {
  useMembersWithPresence,
  useOrganizationPresenceSubscription,
  usePresenceOverlaySync,
} from 'hooks/usePresenceCacheSync';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useFavoriteAgents } from 'utils/useFavoriteAgents';

import { type ActiveFilters, type Status } from '../components/searchControls';

type AvailableFilters = {
  status: { [K in Status]: SearchFilterCondition<'users'>[] };
  team: (teamIds: string[]) => SearchFilterCondition<'users'>[];
  agent: (agentIds: string[]) => SearchFilterCondition<'users'>[];
  role: (roleIds: string[]) => SearchFilterCondition<'users'>[];
};

type UseTeamTableDataParams = {
  page: number;
  activeFilters: ActiveFilters;
};

type UseTeamTableDataReturn = {
  ownId: string | undefined;
  favorites: readonly string[];
  membersWithPresence: readonly SearchUserEnriched[];
  memberCount: number | undefined;
  pageCount: number | undefined;
  loading: boolean;
  isManualRefresh: boolean;
  handleRefreshMembers: () => void;
  toggleFavorite: (isFavorite: boolean, agentId: string) => () => void;
  showUserModal: (user: Partial<User>) => void;
  hasOrganizationMemberAccess: boolean;
  hasPresenceAccess: boolean;
  awayStatuses: AwayStatus[];
};

export const useTeamTableData = ({
  page,
  activeFilters,
}: UseTeamTableDataParams): UseTeamTableDataReturn => {
  const dispatch = useAppDispatch();

  // Redux state
  const ownId = useAppSelector((state) => state.settings.profile.profile?.id);

  const {
    favoriteAgentIds: favorites,
    addFavorite,
    removeFavorite,
  } = useFavoriteAgents();

  const showUserModal = useCallback(
    (user: Partial<User>): void => {
      dispatch(userModalActions.show({ user }));
    },
    [dispatch],
  );

  const hasOrganizationMemberAccess = useHasAccess('organization.member');
  const hasPresenceAccess = useHasAccess(
    [
      'organization.member.presence.status',
      'organization.member.presence.working_media',
    ],
    { strategy: 'any' },
  );

  const flags = useFeatureFlag(['custom-away-status']);

  const [isManualRefresh, setIsManualRefresh] = useState(false);

  const availableFilters: AvailableFilters = useMemo(
    () => ({
      status: {
        working: [
          {
            field: 'is_working',
            operator: 'equals',
            values: [{ value: 'true' }],
          },
          {
            field: 'is_online',
            operator: 'equals',
            values: [{ value: 'true' }],
          },
        ],
        away: [
          {
            field: 'is_working',
            operator: 'equals',
            values: [{ value: 'false' }],
          },
          {
            field: 'is_online',
            operator: 'equals',
            values: [{ value: 'true' }],
          },
        ],
        online: [
          {
            field: 'is_online',
            operator: 'equals',
            values: [{ value: 'true' }],
          },
        ],
        offline: [
          {
            field: 'is_online',
            operator: 'not_equals',
            values: [{ value: 'true' }],
          },
        ],
        'wrap-up': [
          {
            field: 'has_acw',
            operator: 'equals',
            values: [{ value: 'true' }],
          },
        ],
        favorite: [
          {
            field: 'id',
            operator: 'is_one',
            values: favorites.map((id) => ({
              value: id,
            })),
          },
        ],
        assigned: [
          {
            field: 'is_assigned',
            operator: 'equals',
            values: [{ value: 'true' }],
          },
        ],
        unassigned: [
          {
            field: 'is_assigned',
            operator: 'not_equals',
            values: [{ value: 'true' }],
          },
        ],
      },
      team: (teamIds: string[]) => [
        {
          field: 'teams',
          operator: 'includes_one',
          values: teamIds.map((teamId) => ({ value: teamId })),
        },
      ],
      agent: (agentIds) => [
        {
          field: 'id',
          operator: 'is_one',
          values: agentIds.map((agentId) => ({ value: agentId })),
        } satisfies SearchFilterCondition,
      ],
      role: (roleIds) => [
        {
          field: 'roles',
          operator: 'includes_one',
          values: roleIds.map((roleId) => ({ value: roleId })),
        } satisfies SearchFilterCondition,
      ],
    }),
    [favorites],
  );

  const queryConditions: SearchFilterCondition<'users'>[] = useMemo(
    () => [
      ...(activeFilters.status
        ? availableFilters.status[activeFilters.status]
        : []),
      ...(activeFilters.team.length
        ? availableFilters.team(activeFilters.team)
        : []),
      ...(activeFilters.agent.length
        ? availableFilters.agent(activeFilters.agent)
        : []),
      ...(activeFilters.role.length
        ? availableFilters.role(activeFilters.role)
        : []),
    ],
    [activeFilters, availableFilters],
  );

  const searchQueryParams: SearchParameters<'users', true> = useMemo(
    () => ({
      scope: 'users',
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'user_type',
            operator: 'is_one',
            values: [{ value: 'member' }],
          },
          ...queryConditions,
        ],
      },
      sortBy: { field: 'display_name', order: 'asc' },
      enrichmentSpec: ['workload_config'],
      // Search API is 0-indexed, UI is 1-indexed
      page: page - 1,
    }),
    [page, queryConditions],
  );

  const [
    triggerSearch,
    { members, memberCount, pageCount, isLoading, isFetching },
  ] = krakenApi.useLazyReadSearchUsersEnrichedQuery({
    selectFromResult: ({ data, isLoading, isFetching }) => {
      const members = data?.data.results.map(({ result }) => result) ?? [];

      return {
        members,
        memberCount: data?.meta.total_results,
        pageCount: data?.meta.total_pages,
        isLoading,
        isFetching,
      };
    },
  });

  // Initial load - fresh fetch
  useMount(() => {
    triggerSearch(searchQueryParams, false);
  });

  // Filter changes - fresh fetch (invalidate cache)
  useUpdateEffect(() => {
    triggerSearch(searchQueryParams, false);
  }, [queryConditions]);

  // Page changes - use cache if available
  useUpdateEffect(() => {
    triggerSearch(searchQueryParams, true);
  }, [page]);

  const loading = isLoading || isFetching;

  const handleRefreshMembers = useCallback(() => {
    setIsManualRefresh(true);
    triggerSearch(searchQueryParams, false);
  }, [triggerSearch, searchQueryParams]);

  useEffect(() => {
    if (!loading && isManualRefresh) {
      setIsManualRefresh(false);
    }
  }, [loading, isManualRefresh]);

  usePresenceOverlaySync();
  const membersWithPresence = useMembersWithPresence(members);

  useOrganizationPresenceSubscription();

  const { awayStatuses } = presenceApi.useReadAwayStatusesQuery(
    { isDeactivated: false },
    {
      skip: !flags.customAwayStatus,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        awayStatuses: data?.data.customAwayStatuses ?? [],
      }),
    },
  );

  const toggleFavorite = useCallback(
    (isFavorite: boolean, agentId: string) => () => {
      ampli.teamFavoriteToggled({ value: !isFavorite });
      if (isFavorite) {
        removeFavorite(agentId);
      } else {
        addFavorite(agentId);
      }
    },
    [addFavorite, removeFavorite],
  );

  return {
    ownId,
    favorites,
    membersWithPresence,
    memberCount,
    pageCount,
    loading,
    isManualRefresh,
    handleRefreshMembers,
    toggleFavorite,
    showUserModal,
    hasOrganizationMemberAccess,
    hasPresenceAccess,
    awayStatuses,
  };
};
